import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CalendarRange, Users, Receipt, BarChart3, AlertTriangle } from "lucide-react";
import { PnlChart } from "./PnlChart";
import { ProjectCostsPanel } from "./ProjectCostsPanel";
import { eur, hrs, pct, fmtDate, STATUS, currentMonthLocal } from "./format";

const STATUS_RO = { planning: "Planificare", active: "Activ", in_progress: "În derulare", completed: "Finalizat", on_hold: "Suspendat" };

export function ProjectPnlDetail({ projectId, fromMonth, months, onClose, canEdit, settings, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("monthly");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/pnl/project/${projectId}`, { params: { from_month: fromMonth, months } });
      setData(r.data.project);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }, [projectId, fromMonth, months]);
  useEffect(() => { load(); }, [load]);

  const p = data;
  const t = p?.totals || {};
  const st = STATUS[t.status] || STATUS.no_budget;
  const currentMonth = currentMonthLocal();
  const tabBtn = (key, Icon, label) => (
    <button data-testid={`pnl-detail-tab-${key}`} onClick={() => setTab(key)}
      className={`pio-tab flex items-center gap-1.5 ${tab === key ? "is-active" : ""}`}><Icon className="h-3.5 w-3.5" /> {label}</button>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="pnl-detail-dialog">
        {loading || !p ? (
          <div className="py-24 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-start justify-between gap-4 flex-wrap pr-6">
                <div>
                  <div className="font-mono text-[11px] text-slate-400 tracking-wider">{p.project_code} · {STATUS_RO[p.status] || p.status}{p.client_name ? ` · ${p.client_name}` : ""}</div>
                  <DialogTitle className="text-2xl font-bold text-slate-800 leading-tight" data-testid="pnl-detail-title">{p.project_name}</DialogTitle>
                  <DialogDescription className="text-[12px] mt-1">
                    Perioada proiectului: {fmtDate(p.start_date)} → {fmtDate(p.end_date)} · Cheltuieli indirecte: {pct(p.indirect_pct)}{p.indirect_pct_is_default ? " (global)" : ""} · Curs 1 € = {settings?.eur_ron_rate} RON
                  </DialogDescription>
                </div>
                <span className={`pio-badge ${st.badge} text-[12px] px-3 py-1`} data-testid="pnl-detail-status">{st.label}</span>
              </div>
            </DialogHeader>

            {t.status === "over" && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-[12px] text-red-800" data-testid="pnl-detail-over-alert">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                  {t.planned_value > p.budget_total && <>Alocările configurate ({eur(t.planned_value)}) depășesc bugetul total ({eur(p.budget_total)}) cu <b>{eur(t.planned_value - p.budget_total)}</b>. </>}
                  {t.actual_value > p.budget_total && <>Orele pontate ({eur(t.actual_value)}) au depășit bugetul cu <b>{eur(t.actual_value - p.budget_total)}</b>.</>}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mt-2">
              <Kpi testId="pnl-kpi-budget" label="Buget total" value={eur(p.budget_total)} sub={p.has_periods ? `${p.budget_periods.length} perioade` : "fără perioade"} />
              <Kpi testId="pnl-kpi-planned" label="Buget planificat din alocări" value={eur(t.planned_value)} sub={`${hrs(t.planned_hours)} · ${pct(t.planned_pct)} din buget`} accent="#3d68b1" bar={t.planned_pct} />
              <Kpi testId="pnl-kpi-actual" label="Buget realizat din pontaje" value={eur(t.actual_value)} sub={`${hrs(t.actual_hours)} · ${pct(t.actual_pct)} din buget`} accent="#16a34a" bar={t.actual_pct} />
              <Kpi testId="pnl-kpi-cost" label="Cost total" value={eur(t.total_cost)} sub={`manoperă ${eur(t.labour_cost)} · ind. ${eur(t.indirect_cost)} · dir. ${eur(t.direct_costs)} · regie ${eur(t.overhead)}`} accent="#8B6E4A" />
              <Kpi testId="pnl-kpi-margin" label="Marjă reală" value={eur(t.margin)} sub={`buget − cost total · ${pct(t.margin_pct)}`} accent={t.margin >= 0 ? "#16a34a" : "#dc2626"} />
              <Kpi testId="pnl-kpi-margin-ts" label="Marja din pontaje" value={eur(t.margin_timesheet)} sub={`buget − realizat · ${pct(t.margin_timesheet_pct)}`} accent={t.margin_timesheet >= 0 ? "#16a34a" : "#dc2626"} />
            </div>

            {p.budget_periods?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid="pnl-detail-periods">
                {p.budget_periods.map((bp, i) => (
                  <span key={bp.id || i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-[11px] text-slate-700">
                    <CalendarRange className="h-3 w-3 text-slate-400" />
                    {bp.label ? <b>{bp.label}:</b> : null} {bp.start_date ? `${fmtDate(bp.start_date)} → ${fmtDate(bp.end_date)}` : "fără perioadă"} · <b>{eur(bp.budget_amount)}</b>
                  </span>
                ))}
              </div>
            )}

            <div className="surface p-3 mt-4">
              <PnlChart rows={p.rows} currentMonth={currentMonth} />
            </div>

            <div className="pio-tabs mt-4 mb-3">
              {tabBtn("monthly", BarChart3, "Evoluție lunară")}
              {tabBtn("team", Users, `Echipă & alocări (${p.team?.length || 0})`)}
              {tabBtn("costs", Receipt, `Cheltuieli directe (${(p.costs?.length || 0) + (t.legacy_direct > 0 ? 1 : 0)})`)}
            </div>

            {tab === "monthly" && <MonthlyTable rows={p.rows} currentMonth={currentMonth} />}
            {tab === "team" && <TeamTable team={p.team} />}
            {tab === "costs" && (
              <ProjectCostsPanel projectId={p.project_id} costs={p.costs} categories={settings?.cost_categories} legacyDirect={t.legacy_direct || 0}
                canEdit={canEdit} onChanged={() => { load(); onChanged?.(); }} />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Kpi({ label, value, sub, accent = "#334155", bar, testId }) {
  const color = bar == null ? accent : bar > 100 ? "#dc2626" : bar >= 90 ? "#d97706" : accent;
  return (
    <div className="surface p-3" data-testid={testId}>
      <div className="text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-[18px] font-bold tabular-nums mt-0.5" style={{ color }}>{value}</div>
      <div className="text-[11px] text-slate-500 leading-snug mt-0.5">{sub}</div>
      {bar != null && <div className="pio-progress mt-2"><div style={{ width: `${Math.min(100, bar)}%`, background: color }} /></div>}
    </div>
  );
}

function MonthlyTable({ rows, currentMonth }) {
  const num = (v, d = 0) => (v || 0).toLocaleString("ro-RO", { maximumFractionDigits: d });
  return (
    <div className="overflow-x-auto surface" data-testid="pnl-monthly-table">
      <table className="pio-table pio-table-static whitespace-nowrap">
        <thead>
          <tr>
            <th>Lună</th><th className="text-right">Ore lună</th>
            <th className="text-right text-[#3d68b1]">Plan ore</th><th className="text-right text-[#3d68b1]">Plan €</th>
            <th className="text-right text-green-700">Real ore</th><th className="text-right text-green-700">Real €</th>
            <th className="text-right">Buget lună</th><th className="text-right">Buget cumulat</th><th className="text-right text-[#3d68b1]">Plan cumulat</th>
            <th className="text-right">Manoperă</th><th className="text-right">Indirecte</th><th className="text-right">Directe</th><th className="text-right">Regie</th>
            <th className="text-right">Cost total</th><th className="text-right">Marjă</th><th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const s = STATUS[r.status] || STATUS.no_budget;
            const isNow = r.month === currentMonth;
            return (
              <tr key={r.month} data-testid={`pnl-month-row-${r.month}`} className={isNow ? "bg-amber-50/60" : ""}>
                <td className="font-semibold text-slate-700">{r.label}{isNow && <span className="ml-1 text-[9px] text-amber-600 uppercase">azi</span>}</td>
                <td className="text-right tabular-nums text-slate-400">{num(r.month_hours, 1)}</td>
                <td className="text-right tabular-nums text-[#3d68b1]">{num(r.planned_hours, 1)}</td>
                <td className="text-right tabular-nums text-[#3d68b1] font-semibold">{num(r.planned_value)}</td>
                <td className="text-right tabular-nums text-green-700">{num(r.actual_hours, 1)}</td>
                <td className="text-right tabular-nums text-green-700 font-semibold">{num(r.actual_value)}</td>
                <td className="text-right tabular-nums">{num(r.budget)}</td>
                <td className="text-right tabular-nums text-slate-500">{num(r.budget_cum)}</td>
                <td className={`text-right tabular-nums ${r.budget_cum > 0 && r.planned_cum > r.budget_cum ? "text-red-600 font-semibold" : "text-slate-500"}`}>{num(r.planned_cum)}</td>
                <td className="text-right tabular-nums">{num(r.actual_cost)}</td>
                <td className="text-right tabular-nums">{num(r.indirect_cost)}</td>
                <td className="text-right tabular-nums">{num(r.direct_costs)}</td>
                <td className="text-right tabular-nums">{num(r.overhead)}</td>
                <td className="text-right tabular-nums font-semibold">{num(r.total_cost)}</td>
                <td className={`text-right tabular-nums font-semibold ${r.margin >= 0 ? "text-green-700" : "text-red-600"}`}>{num(r.margin)}</td>
                <td><span className={`pio-badge ${s.badge}`}>{s.label}</span></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TeamTable({ team }) {
  return (
    <div className="overflow-x-auto surface" data-testid="pnl-team-table">
      <table className="pio-table pio-table-static whitespace-nowrap">
        <thead>
          <tr>
            <th>Angajat</th><th className="text-right">Alocat</th><th>Perioadă alocare</th><th className="text-right">Fee rate</th>
            <th className="text-right text-[#3d68b1]">Ore plan</th><th className="text-right text-[#3d68b1]">Valoare plan</th>
            <th className="text-right text-green-700">Ore real</th><th className="text-right text-green-700">Valoare real</th><th className="text-right">Cost intern real</th>
          </tr>
        </thead>
        <tbody>
          {(team || []).length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-400">Nicio persoană alocată și niciun pontaj.</td></tr>}
          {(team || []).map((m) => (
            <tr key={m.employee_id} data-testid={`pnl-team-row-${m.employee_id}`}>
              <td><div className="font-semibold text-slate-800">{m.name}</div><div className="text-[11px] text-slate-400">{m.job_title}</div></td>
              <td className="text-right tabular-nums">{m.allocation_periods ? "pe perioade" : m.pct > 0 ? `${m.pct}%` : <span className="text-slate-300">—</span>}</td>
              <td className="text-[12px] text-slate-500">{m.allocation_periods ? m.allocation_periods.map((x) => <div key={x.start_date}>{fmtDate(x.start_date)} → {fmtDate(x.end_date)}: {x.pct}%</div>) : m.permanent ? "Permanent" : m.start_date || m.end_date ? `${fmtDate(m.start_date)} → ${fmtDate(m.end_date)}` : m.pct > 0 ? "Pe durata proiectului" : "—"}</td>
              <td className="text-right tabular-nums">{eur(m.fee_rate, 2)}</td>
              <td className="text-right tabular-nums text-[#3d68b1]">{hrs(m.planned_hours)}</td>
              <td className="text-right tabular-nums text-[#3d68b1] font-semibold">{eur(m.planned_value)}</td>
              <td className="text-right tabular-nums text-green-700">{hrs(m.actual_hours)}</td>
              <td className="text-right tabular-nums text-green-700 font-semibold">{eur(m.actual_value)}</td>
              <td className="text-right tabular-nums text-slate-600">{eur(m.actual_cost)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
